import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { ChevronLeft, ChevronRight } from '@/components/icons/HugeIcons';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useHaptics } from '../../../hooks/useHaptics';
import { useTheme } from '../../../hooks/useTheme';
import { useLocale } from '../../../hooks/useLocale';

export interface SettingsRowProps {
  icon?: React.ReactNode;
  label: string;
  description?: string;
  value?: string;
  onPress?: () => void;
  showChevron?: boolean;
  isLast?: boolean;
  rightElement?: React.ReactNode;
  destructive?: boolean;
  selected?: boolean;
}

export function SettingsRow({
  icon,
  label,
  description,
  value,
  onPress,
  showChevron = false,
  isLast = false,
  rightElement,
  destructive = false,
  selected,
}: SettingsRowProps) {
  const { theme } = useTheme();
  const haptics = useHaptics();
  const { isRTL } = useLocale();
  const textAlign = isRTL ? 'right' : 'left';
  const flexDirection = isRTL ? 'row-reverse' : 'row';
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  const handlePress = () => {
    if (!onPress) return;
    haptics.trigger('light');
    onPress();
  };

  return (
    <Pressable
      accessible={Boolean(onPress)}
      accessibilityRole={onPress ? selected === undefined ? 'button' : 'radio' : undefined}
      accessibilityLabel={label}
      accessibilityHint={description}
      accessibilityState={selected === undefined ? undefined : { checked: selected }}
      onPress={onPress ? handlePress : undefined}
      disabled={!onPress}
      style={({ pressed }) => [
        styles.row,
        { flexDirection },
        !isLast && {
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: theme.borderSubtle,
        },
        pressed && onPress ? { backgroundColor: theme.surface } : null,
      ]}
    >
      {icon ? <View style={styles.rowIcon}>{icon}</View> : null}
      <View style={styles.rowCopy}>
        <Text
          style={[
            styles.rowLabel,
            { color: destructive ? colors.danger : theme.text, textAlign },
          ]}
        >
          {label}
        </Text>
        {description ? (
          <Text style={[styles.rowDescription, { color: theme.textTertiary, textAlign }]}>
            {description}
          </Text>
        ) : null}
      </View>
      <View style={[styles.rowRight, { flexDirection }]}>
        {rightElement}
        {value ? (
          <Text style={[styles.rowValue, { color: theme.textTertiary, textAlign }]}>{value}</Text>
        ) : null}
        {showChevron ? (
          <Chevron size={18} color={theme.textTertiary} strokeWidth={1.75} />
        ) : null}
      </View>
    </Pressable>
  );
}

export function SectionCard({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  return (
    <View style={[styles.card, { backgroundColor: theme.surface }]}>{children}</View>
  );
}

export function SectionHeader({ title }: { title: string }) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  return (
    <Text style={[styles.sectionHeader, { color: theme.textTertiary, textAlign: isRTL ? 'right' : 'left' }]}>{title}</Text>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: spacing[4],
    minHeight: 50,
  },
  rowIcon: {
    width: 28,
    alignItems: 'center',
    marginEnd: spacing[3],
  },
  rowLabel: {
    fontSize: 16,
    fontWeight: '400',
  },
  rowCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
    paddingEnd: spacing[3],
  },
  rowDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  rowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    maxWidth: '42%',
  },
  rowValue: {
    fontSize: 14,
    flexShrink: 1,
  },
  card: {
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: spacing[5],
    marginBottom: spacing[2],
    marginStart: spacing[1],
  },
});
